import { ApiError } from '@/services/api';

/**
 * A booking the API refused with an explanation of its own — for example a booking type
 * that obeys the user's working profile and bank holidays. Returns the server's message,
 * or null when the failure is a generic one the caller should report itself.
 */
export function getBookingValidationMessage(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.statusCode !== 400) return null;

  const message = error.details?.trim();
  if (!message) return null;

  return error.endpoint.toLowerCase().includes('/bookings') ? message : null;
}

/**
 * Whether the failure belongs to the booking form: the API refused the booking with a
 * reason of its own, so the form stays open with that reason in a dialog rather than
 * closing on a refusal the user still has to act on.
 */
export function isBookingValidationError(error: unknown): boolean {
  return getBookingValidationMessage(error) !== null;
}

type Listener = (message: string) => void;

let listener: Listener | null = null;

/**
 * Registers the dialog that presents these messages. Only one is mounted, at the app
 * root, so any save path reports a refusal the same way.
 */
export function subscribeToBookingValidationAlerts(next: Listener): () => void {
  listener = next;
  return () => {
    if (listener === next) listener = null;
  };
}

/** Shows the message in the dialog. False when no dialog is mounted to show it. */
export function showBookingValidationAlert(message: string): boolean {
  if (!listener) return false;
  listener(message);
  return true;
}

/**
 * Presents a refused booking in the dialog. True when the error was a refusal the dialog
 * has taken over, so the caller must not also raise a generic failure toast.
 */
export function reportBookingValidationError(error: unknown): boolean {
  const message = getBookingValidationMessage(error);
  return message !== null && showBookingValidationAlert(message);
}
